/**
 * Переключатели темы, плотности и поле даты.
 *
 * Плотность — параметр, а не стиль (docs/04, §1.4): один и тот же интерфейс
 * должен уметь показать 34 строки на экране и остаться читаемым при 24.
 */

import * as React from "react";
import { Monitor, Moon, Sun } from "lucide-react";

import {
  getDensity,
  getTheme,
  setDensity,
  setTheme,
  type Density,
  type Theme,
} from "@/shared/lib/theme";
import { SegmentedControl } from "@/shared/ui/Choice";
import { TextField, type TextFieldProps } from "@/shared/ui/TextField";

export function ThemeToggle(): React.ReactElement {
  const [theme, setThemeState] = React.useState<Theme>(() => getTheme());

  return (
    <SegmentedControl<Theme>
      label="Тема оформления"
      value={theme}
      onValueChange={(next) => {
        setThemeState(next);
        setTheme(next);
      }}
      options={[
        { value: "light", label: <Sun className="size-[var(--pt-icon)]" />, ariaLabel: "Светлая" },
        { value: "dark", label: <Moon className="size-[var(--pt-icon)]" />, ariaLabel: "Тёмная" },
        {
          value: "system",
          label: <Monitor className="size-[var(--pt-icon)]" />,
          ariaLabel: "Как в системе",
        },
      ]}
    />
  );
}

export function DensityToggle(): React.ReactElement {
  const [density, setDensityState] = React.useState<Density>(() => getDensity());

  return (
    <SegmentedControl<Density>
      label="Плотность интерфейса"
      value={density}
      onValueChange={(next) => {
        setDensityState(next);
        setDensity(next);
      }}
      options={[
        { value: "compact", label: "Плотно" },
        { value: "normal", label: "Обычно" },
        { value: "comfortable", label: "Просторно" },
      ]}
    />
  );
}

export interface DatePickerProps extends Omit<TextFieldProps, "type" | "value" | "onChange"> {
  value: string | null;
  onValueChange: (value: string | null) => void;
}

/**
 * Поле даты.
 *
 * В основе — нативный `input[type=date]`: он даёт клавиатурный ввод, локальный
 * формат и календарь ОС бесплатно. Собственный календарь появится там, где
 * нужны диапазоны и рабочие календари, — в фазе представлений.
 */
export function DatePicker({
  value,
  onValueChange,
  ...props
}: DatePickerProps): React.ReactElement {
  return (
    <TextField
      type="date"
      value={value ?? ""}
      onChange={(event) => onValueChange(event.target.value || null)}
      {...props}
    />
  );
}
